import React, { useCallback } from 'react';
import { FetchResult, MutationFn, MutationOptions } from 'react-apollo';
import { core_apimessages_Error as Error } from '../schemas/rql';
import { parseGraphqlErrors } from '@reverbdotcom/commons/src/parse_graphql_errors';

interface MutationResult<MutationData> {
  data: MutationData;
  errors: Error[];
  loading: boolean;
}

type MutationTuple<MutationData, Variables> = [
  (variables: MutationOptions<MutationData, Variables>) => Promise<FetchResult<MutationData>>,
  MutationResult<MutationData>,
];

/**
 * Port of [Apollo useMutation](https://www.apollographql.com/docs/react/data/mutations)
 * hook. Takes a mutation provided by a higher-order component graphql-tag mutation
 * and provides the fetch result, state and wrapped Core Errors as a result.
 *
 * @example
 * const [addToCart, { data, loading, errors }] = useUpdateOrderQuantity(props.editCart);
 *
 * <button onClick={addToCart}>Add to Cart</button>
 */
export function useMutation<MutationData, Variables>(
  mutation: MutationFn<MutationData, Variables>,
): MutationTuple<MutationData, Variables> {
  const [result, setResult] = React.useState<MutationResult<MutationData>>({
    data: null,
    errors: [],
    loading: false,
  });

  const execute = useCallback(async (variables: MutationOptions<MutationData, Variables>) => {
    setResult({
      data: null,
      errors: [],
      loading: true,
    });

    try {
      const fetchResult = await mutation(variables);
      if (fetchResult) {
        setResult({
          data: fetchResult.data,
          errors: [],
          loading: false,
        });
        return fetchResult;
      }
      setResult({
        data: null,
        errors: [],
        loading: false,
      });
    } catch (e) {
      setResult({
        data: null,
        errors: parseGraphqlErrors(e),
        loading: false,
      });
    }
    return null;
  }, [mutation]);

  return [execute, result];
}
